import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { axiosCompanyInstance } from "src/config/axios";
import { ErrorResponse } from "src/utils/response";
import { FormErrorObject, LanguageType } from "src/utils/types";
import { RQ_CUSTOMER_LIST_KEY } from "./useCustomerList";

export interface CustomerUpsertBillingProfile {
    id?: number;
    profileName: string;
    companyName: string;
    vatId: string;
    address: {
        address1: string;
        address2: string | null;
        city: string;
        postalCode: string;
    };
}

export interface CustomerUpsertToken {
    id?: number;
    rfid: string;
    isActive: boolean;
    validFrom: string | null;
    validTo: string | null;
    billingProfile: CustomerUpsertBillingProfile | null;
}

export interface CustomerUpsertRequest {
    id?: string;
    email: string;
    firstName: string;
    lastName: string;
    customerGroup: string | null;
    active: boolean;
    creditLimit: number | null;
    tokens: CustomerUpsertToken[];
    lang: LanguageType;
}

interface OwnProps {
    onSuccess?: () => void;
    onError?: (errors: FormErrorObject) => void;
}

export const useCustomerCreateMutation = ({ onSuccess, onError }: OwnProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMutation(
        async (values: CustomerUpsertRequest) => {
            const res = await axiosCompanyInstance.post(`customer`, values);

            return res?.data || null;
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries([RQ_CUSTOMER_LIST_KEY]);
                if (onSuccess) {
                    onSuccess();
                }
            },
            onError: (err: AxiosError<ErrorResponse>) => {
                if (err.response?.data.error) {
                    enqueueSnackbar(t("shared.validation.api." + err.response?.data.error), {
                        variant: "error",
                    });
                }
                if (err.response?.data.errors && onError) {
                    onError(err.response?.data.errors);
                }
            },
        }
    );
};
